import { HeadFC, PageProps } from "gatsby";
import Layout from "../../components/Layout";
import React, { useEffect, useState } from "react";
import GridContainer from "@upsun/react-component-library/src/components/GridContainer";
import GridCol from "@upsun/react-component-library/src/components/GridCol";
import Heading from "@upsun/react-component-library/src/components/Heading";
import HeroCentered from "@upsun/react-component-library/src/components/HeroCentered";
import CalloutRainbow from "@upsun/react-component-library/src/components/CalloutRainbow";
import TextWithHighlight from "@upsun/react-component-library/src/components/TextWithHighlight";
import { center } from "../../utils/global-styles";
import { css } from "@emotion/react";
import { fontSizeLineHeight, mediaQueries, pxToRem, respSpacing, subtitle as subtitleStyles } from "@upsun/react-component-library/src/styles";
import SupportTable from "../../components/pricing/SupportTable";
import UserLicensesTable from "../../components/pricing/UserLicensesTable";
import BillingTable from "../../components/pricing/BillingTable";
import ProjectsTable from "../../components/pricing/ProjectsTable";
import CICDTable from "../../components/pricing/CICDTable";
import EnvironmentsTable from "../../components/pricing/EnvironmentsTable";
import SecurityTable from "../../components/pricing/SecurityTable";
import ObservabilityTable from "../../components/pricing/ObservabilityTable";
import AlwaysIncludedTable from "../../components/pricing/AlwaysIncludedTable";
import EdgeTable from "../../components/pricing/EdgeTable";
import StorageTable from "../../components/pricing/StorageTable";
import { dropdownOptions, pricingInfo } from "../../data/pricing-page-data";
import Button from "@upsun/react-component-library/src/components/Button";
import UserLicensesDetails from "../../components/pricing/UserLicensesDetails";
import { gridCard, row } from "../../utils/pricing-page-styles";
import CPUandMemoryDetails from "../../components/pricing/CPUandMemoryDetails";
import useLocalCurrency from "../../hooks/useLocalCurrency";
import CurrencySelector from "../../components/pricing-calculator/CurrencySelector";
import PricingPagePill from "../../components/pricing/PricingPagePill";
import SidebarCallout from "../../components/pricing/SidebarCallout";
import ProjectFee from "../../components/pricing/ProjectFee";
import { SEO } from "../../components/Seo";
import JumplinkBar from "@upsun/react-component-library/src/components/JumplinkBar";
import AllServicesTable from "../../components/pricing/AllServicesTable";
import FreeTrial from "../../components/pricing/FreeTrial";

const smallTxt = css`
  margin-top: ${pxToRem(12)};
  font-size: ${pxToRem(12)};
`;

const PricingPage: React.FC<PageProps> = ({ location }) => {
  const [currency, setCurrency] = useState("EUR");

  useEffect(() => {
    useLocalCurrency(setCurrency);
  }, []);

  const noMargin = css`
    margin-bottom: 0;
  `;

  const align = css`
    display: flex;
    align-items: center;
  `;

  const bigMargin = css`
    margin-bottom: ${pxToRem(80)};
  `;

  const centerTablet = css`
    text-align: center;

    ${mediaQueries.lg} {
      text-align: left;
    }
  `;

  const marginTop = css`
    margin-top: ${pxToRem(40)};

    ${mediaQueries.lg} {
      margin-top: ${pxToRem(80)};
    }
  `;

  const CalcIcon = () => {
    const iconStyles = css`
      position: relative;
      margin-right: ${pxToRem(4)};

      ${mediaQueries.lg} {
        top: ${pxToRem(1)};
      }
    `;

    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        css={iconStyles}
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.888672 14.2222C0.888672 15.2 1.68867 16 2.66645 16H13.3331C14.3109 16 15.1109 15.2 15.1109 14.2222V1.77778C15.1109 0.8 14.3109 0 13.3331 0H2.66645C1.68867 0 0.888672 0.8 0.888672 1.77778V14.2222ZM2.66645 14.2222V1.77778H13.3331V14.2222H2.66645ZM6.22201 9.77778C6.22201 10.2687 5.82404 10.6667 5.33312 10.6667C4.8422 10.6667 4.44423 10.2687 4.44423 9.77778C4.44423 9.28686 4.8422 8.88889 5.33312 8.88889C5.82404 8.88889 6.22201 9.28686 6.22201 9.77778ZM5.33312 13.3333C5.82404 13.3333 6.22201 12.9354 6.22201 12.4444C6.22201 11.9535 5.82404 11.5556 5.33312 11.5556C4.8422 11.5556 4.44423 11.9535 4.44423 12.4444C4.44423 12.9354 4.8422 13.3333 5.33312 13.3333ZM8.88867 9.77778C8.88867 10.2687 8.4907 10.6667 7.99978 10.6667C7.50886 10.6667 7.11089 10.2687 7.11089 9.77778C7.11089 9.28686 7.50886 8.88889 7.99978 8.88889C8.4907 8.88889 8.88867 9.28686 8.88867 9.77778ZM7.99978 13.3333C8.4907 13.3333 8.88867 12.9354 8.88867 12.4444C8.88867 11.9535 8.4907 11.5556 7.99978 11.5556C7.50886 11.5556 7.11089 11.9535 7.11089 12.4444C7.11089 12.9354 7.50886 13.3333 7.99978 13.3333ZM11.5553 9.77778C11.5553 10.2687 11.1574 10.6667 10.6664 10.6667C10.1755 10.6667 9.77756 10.2687 9.77756 9.77778C9.77756 9.28686 10.1755 8.88889 10.6664 8.88889C11.1574 8.88889 11.5553 9.28686 11.5553 9.77778ZM10.6664 13.3333C11.1574 13.3333 11.5553 12.9354 11.5553 12.4444C11.5553 11.9535 11.1574 11.5556 10.6664 11.5556C10.1755 11.5556 9.77756 11.9535 9.77756 12.4444C9.77756 12.9354 10.1755 13.3333 10.6664 13.3333ZM4.88867 7.82222H11.1109C11.822 7.82222 12.4442 7.2 12.4442 6.48889V3.82222C12.4442 3.11111 11.822 2.48889 11.1109 2.48889H4.88867C4.17756 2.48889 3.55534 3.11111 3.55534 3.82222V6.48889C3.55534 7.2 4.17756 7.82222 4.88867 7.82222ZM10.6664 6.04444H5.33312V4.26667H10.6664V6.04444Z"
          fill="currentColor"
        />
      </svg>
    );
  };

  const calloutMarginStyles = css`
    margin-top: ${pxToRem(48)};
    margin-bottom: ${pxToRem(80)};
  `;

  const calloutMarginStylesAlt = css`
    margin-top: ${pxToRem(48)};
    margin-bottom: ${pxToRem(32)};
  `;

  const commitmentDisclaimerStyles = css`
    ${respSpacing("margin-block-start", "xl")};
    ${fontSizeLineHeight(13,24)};
  `;

  const allServicesMarginStyles = css`
    ${respSpacing("margin-block-start", "3xl")};
  `;

  const heroWrapperStyles = css`
    padding-top: ${pxToRem(40)};
    padding-bottom: ${pxToRem(40)};
    text-align: center;

    ${mediaQueries.lg} {
      padding-top: ${pxToRem(80)};
      padding-bottom: ${pxToRem(80)};
    }
  `;

  const heroColStyles = css`
    > :last-child {
      margin-bottom: 0;
    }

    ${mediaQueries.sm} {
      h1 {
        margin-bottom: ${pxToRem(24)};
      }
    }
  `;

  const heroBtnWrapperStyles = css`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: ${pxToRem(20)};
  `;

  const subtitleWrapperStyles = css`
    ${respSpacing('margin-block-end', 'sm')};
    ${subtitleStyles};
  `;

  const trialBtnStyles = css`
    margin-bottom: ${pxToRem(32)};

    ${mediaQueries.lg} {
      margin-bottom: 0;
    }
  `;

  return (
    <Layout cta subNav={{ links: [] }} location={location.pathname}>
      <HeroCentered 
          title="Predictable and transparent pricing that scales with you"
          subtitle="Choose the resources your application needs and pay only for what you provision. No plans. No bundles."
          tag="Upsun Cloud"
          btnText="Contact enterprise sales"
          btnHref="/contact-us/"
      />

      <GridContainer>
        <GridCol
          colStart={3}
          colEnd={11}
          colStartLg={1}
          colEndLg={6}
          colStartXl={2}
          colEndXl={5}
          css={centerTablet}
        >
          <Heading
            level={2}
          >
            <TextWithHighlight orig="Try it now" />
          </Heading>
          <p>
            Try Upsun with a free trial and ongoing credits that cover your first project. After that, you only pay for the resources you provision.
          </p>
          <Button href="https://auth.upsun.com/register" text="Deploy your first project" level="tertiary" css={trialBtnStyles}/>
        </GridCol>
        <GridCol
          colStart={1}
          colEnd={13}
          colStartLg={6}
          colEndLg={13}
          colStartXl={5}
          colEndXl={12}
        >
          <PricingPagePill>
            <FreeTrial currency={currency} pricingInfo={pricingInfo} />
          </PricingPagePill>
        </GridCol>
      </GridContainer>
      <JumplinkBar mode="jumplinks" links={dropdownOptions} />
      <GridContainer>
        <GridCol
          colStart={1}
          colEnd={13}
          colStartLg={1}
          colEndLg={13}
          colStartXl={2}
          colEndXl={12}
        >
          <CurrencySelector currency={currency} setCurrency={setCurrency} />
        </GridCol>
        <GridCol
          colStart={3}
          colEnd={11}
          colStartLg={1}
          colEndLg={6}
          colStartXl={2}
          colEndXl={5}
          css={centerTablet}
        >
          <Heading
            level={2}
            text={"Project resources"}
            id="project-resources"
          />
          <p>
            Customize and adjust project resources as needed, scaling vertically 
            or horizontally to support application growth.
          </p>
          <SidebarCallout icon="leaf">
              <h3>Green hosting incentive</h3>
              Receive a <strong>3% sustainability credit</strong> when you 
              deploy to one of six eligible greener regions. The credit is 
              applied to your monthly bill for provisioned resources. <a 
              href="/blog/greener-region-discount/">Learn how</a>
          </SidebarCallout>
        </GridCol>
        <GridCol
          colStart={1}
          colEnd={13}
          colStartLg={6}
          colEndLg={13}
          colStartXl={5}
          colEndXl={12}
        >
          <PricingPagePill>
            <CPUandMemoryDetails currency={currency} />
          </PricingPagePill>
        </GridCol>
        <GridCol
          colStart={1}
          colEnd={13}
          colStartLg={6}
          colEndLg={13}
          colStartXl={5}
          colEndXl={12}
        >
          <PricingPagePill css={gridCard}>
            <ProjectFee pricingInfo={pricingInfo} currency={currency} />
          </PricingPagePill>
        </GridCol>

        <GridCol
          colStart={1}
          colEnd={13}
          colStartLg={6}
          colEndLg={13}
          colStartXl={5}
          colEndXl={12}
        >
          <PricingPagePill>
            <Heading level={4} text={"Disk storage"} />
            <div css={row}>
              <p>Billed/environment</p>
              <p>{pricingInfo.diskStorage[currency]}/GB/month</p>
            </div>
            <Heading level={4} css={align}>
              Backups
            </Heading>
            <div css={row}>
              <p>Billed/backup</p>
              <p>{pricingInfo.backups[currency]}/GB/month</p>
            </div>
            <Heading level={4} css={align}>
              Object storage*
            </Heading>
            <div css={row}>
              <p css={noMargin}>Billed on allocated capacity per environment, 512 GB minimum</p>
              <p css={noMargin}>{pricingInfo.objectStorageEnv[currency]}/GB/month</p>
            </div>
            <div css={row}>
              <p css={noMargin}>Backups, billed on allocated capacity</p>
              <p css={noMargin}>{pricingInfo.objectStorageBackups[currency]}/GB/month</p>
            </div>
            <p css={[smallTxt, noMargin]}>*Object storage isn’t available on trial projects. <a href="https://developer.upsun.com/docs/manage-resources/object-storage">Learn more.</a></p>
          </PricingPagePill>
        </GridCol>
      </GridContainer>

      <CalloutRainbow 
        title="Save more with commitment" 
        body="Commitments help teams balance predictable cloud spend with the flexibility to scale as projects evolve. Learn how commitment models work and how to budget confidently as usage grows." 
        btnHref="/understanding-commitments/" 
        btnText="Learn about commitments" 
        // @ts-ignore
        css={calloutMarginStyles}
       />
       
      <GridContainer>
        <GridCol
          colStart={3}
          colEnd={11}
          colStartLg={1}
          colEndLg={6}
          colStartXl={2}
          colEndXl={5}
          css={centerTablet}
        >
          <Heading
            level={2}
            text={"User licenses"}
            id="user-licenses"
          />
          <p>Efficiently extend and manage user permissions.</p>

          <SidebarCallout icon="percent">
            <h3>First project incentive</h3>
            <strong>Save up to $19/month</strong>—the equivalent cost of your 
            first user license and first project fee—automatically. <a 
            href="/faq/#whats-the-upsun-first-project-incentive">Ts & Cs apply</a>
          </SidebarCallout>
        </GridCol>
        <GridCol
          colStart={1}
          colEnd={13}
          colStartLg={6}
          colEndLg={13}
          colStartXl={5}
          colEndXl={12}
          css={[bigMargin]}
        >
          <PricingPagePill>
            <UserLicensesDetails currency={currency} />
          </PricingPagePill>
        </GridCol>




        <GridCol colStart={1} colEnd={13} colStartLg={4} colEndLg={10}>
          <Heading
            level={2}
            text={"All features"}
            id="all-features"
            css={[center, marginTop]}
          />
          <Heading level={4} text={"User management"} />
          <UserLicensesTable currency={currency} />
          <Heading level={4} text={"Organization support"} />
          <SupportTable />
          <Heading level={4} text={"Projects"} />
          <ProjectsTable currency={currency} />
          <Heading level={4} text={"Environments"} />
          <EnvironmentsTable currency={currency} />
          <Heading level={4} text={"CI/CD"} />
          <CICDTable currency={currency} />
          <Heading level={4} text={"Disk storage"} />
          <StorageTable currency={currency} />
          <Heading level={4} text={"Edge"} />
          <EdgeTable currency={currency} />
          <Heading
            level={4}
            text={"Observability and performance monitoring"}
          />
          <ObservabilityTable currency={currency} />
          <Heading level={4} text={"Security and compliance"} />
          <p>We support customers’ compliance efforts under GDPR, CCPA, and other global data protection laws. Upsun also complies with several standards related to data privacy and security, like SOC2 Type 2, PCI Level 1, and ISO 270001. More information is available on <a href="/trust-center/">our trust center</a>.</p>
          <SecurityTable />
          <Heading level={4} text={"Billing and payments"} />
          <BillingTable />
        </GridCol>
      </GridContainer>
     
      <CalloutRainbow 
        title="Enterprise-ready. Always." 
        body="All features are enterprise-grade across the board. If you have special requirements for billing or scaling, our enterprise sales team is ready to help." 
        btnHref="/contact-us/" 
        btnText="Contact enterprise sales"  
        // @ts-ignore
        css={calloutMarginStylesAlt}
      />

     <GridContainer>
        <GridCol colStart={1} colEnd={13} colStartLg={4} colEndLg={10}>
          <Heading
            level={2}
            css={[center, marginTop]}
          >
            <TextWithHighlight orig="Included with _every_ project" />
          </Heading>
          <AlwaysIncludedTable />
        </GridCol>
      </GridContainer>

      <GridContainer>
        <GridCol colStart={1} colEnd={13} colStartLg={4} colEndLg={10}>
          <Heading level={2} css={[center, allServicesMarginStyles]} text={"All services"} id="all-services" />
          <p css={center}>Enhance your experience with our comprehensive Application Services and flexible Onboarding options designed to support every stage of your journey.</p>
          <AllServicesTable />
          <p css={[commitmentDisclaimerStyles, center]}>
            <sup>†</sup>These features may require a <a href="/understanding-commitments/">minimal commitment</a>. Contact sales to discuss.
          </p>
        </GridCol>
      </GridContainer>
    </Layout>
  );
};

export const Head: HeadFC = ({ location }) => (
  <SEO
    title={"Upsun PaaS predictable pricing | Upsun"}
    description={
      "Learn more about Upsun's predictable pricing. Empowering development teams with the flexibility to build diverse applications on a single, self-service PaaS."
    }
    pathname={location.pathname}
  />
);

export default PricingPage;
